import { useState, useEffect, useRef, useCallback } from 'react';
import {
    Pencil, Plus, Wand2, X, Save, Trash2, Check,
    Loader2, Sparkles, AlertTriangle, PenLine, Eye
} from 'lucide-react';
import api from '../../services/api';
import useCustomStore from '../../store/customStore';
import QuestionCreator from '../quiz-code/QuestionCreator';
import QuickEditDiffPanel from '../admin/QuickEditDiffPanel';
import { callAiWithPaidGate } from '../admin/aiPaidGate';
import ConfirmDialog from '../ui/ConfirmDialog';
import QuestionEditPanel from '../quiz/QuestionEditPanel';
import RichInput from '../content/RichInput';
import useCreditPacks from '../../hooks/useCreditPacks';
import AiQuotaPill from '../ui/AiQuotaPill';
import SujetDocsButton from './SujetDocsButton';
import BuyPackButton from './BuyPackButton';

/** Pull plain text out of a blocks array (paragraph only, strip HTML). */
function blocksToText(blocks) {
    if (!Array.isArray(blocks)) return '';
    return blocks
        .filter(b => b.type === 'paragraph' && b.isActive !== false)
        .map(b => (b.content || '').replace(/<[^>]+>/g, ''))
        .join('\n')
        .trim();
}

/** Extract paragraph HTML (<strong>/<em>/<br> preserved), joined for RichInput. */
function blocksToHtml(blocks) {
    if (!Array.isArray(blocks)) return '';
    return blocks
        .filter(b => b.type === 'paragraph' && b.isActive !== false)
        .map(b => b.content || '')
        .join('<br><br>');
}

/** Turn HTML back into paragraph blocks, preserving <strong>/<em>. Splits on <br><br>. */
function htmlToBlocks(html, originalBlocks = []) {
    const others = (originalBlocks || []).filter(b => b.type !== 'paragraph');
    const cleaned = (html || '').trim();
    if (!cleaned && others.length === 0) return [];
    if (!cleaned) return others;
    // Normalize self-closing br + strip trailing empty <div></div> artifacts from contentEditable
    const norm = cleaned
        .replace(/<div><br\s*\/?\s*><\/div>/gi, '<br>')
        .replace(/<div>(.*?)<\/div>/gi, '$1<br>')
        .replace(/<br\s*\/?>/gi, '<br>');
    const parts = norm.split(/<br>\s*<br>/i).map(p => p.trim()).filter(Boolean);
    const paras = parts.map(p => ({ type: 'paragraph', content: p, isActive: true }));
    return paras.length > 0 ? [...paras, ...others] : others;
}

/** Build a paragraph block from plain text (preserves non-paragraph blocks if provided). */
function textToBlocks(text, originalBlocks = []) {
    const others = (originalBlocks || []).filter(b => b.type !== 'paragraph');
    const trimmed = (text || '').trim();
    if (!trimmed && others.length === 0) return [];
    const para = { type: 'paragraph', content: trimmed, isActive: true };
    return trimmed ? [para, ...others] : others;
}

const ANSWERS_LENGTH_INSTRUCTION = [
    'Reformule les réponses pour que les 4 réponses aient une longueur SIMILAIRE (±20% en nombre de mots), sans changer la véracité ni la précision technique d\'aucune réponse. Les distracteurs doivent rester plausibles et pédagogiquement utiles. Ne change PAS la bonne réponse.',
    'Si tu reformules le texte d\'une réponse, reformule AUSSI l\'explication pour qu\'elle reste parfaitement cohérente avec la nouvelle formulation (mêmes termes, même raisonnement).',
    'Dans l\'explication, ne cite JAMAIS les réponses par leur lettre (A, B, C, D) ni par une expression comme "la réponse A" / "réponse B". Cite TOUJOURS la VALEUR TEXTUELLE de la réponse (ex. "le président du conseil départemental" plutôt que "la réponse C"). Si l\'explication actuelle contient de telles citations par lettre, réécris-les pour utiliser le texte de la réponse concernée.',
].join(' ');

/**
 * Unified "Éditer le sujet" modal.
 *
 * Props:
 *  - sousSujet: { id, custom_ss_id, label, nb_questions, ... }
 *  - isAdmin: bool (unlocks the "Améliorer" section)
 *  - onClose: () => void
 *  - onRefresh: () => void  (called after changes so parent list re-fetches)
 */
export default function SujetEditModal({ sousSujet, thematiqueId, isAdmin, onClose, onRefresh, onObsolete }) {
    const { renameSousSujet } = useCustomStore();

    // === Rename state ===
    const [label, setLabel] = useState(sousSujet.label || '');
    const [editingName, setEditingName] = useState(false);
    const [labelDraft, setLabelDraft] = useState(sousSujet.label || '');
    const [savingLabel, setSavingLabel] = useState(false);

    // === Questions list (quick edit) ===
    const [questions, setQuestions] = useState([]);
    const [loadingQuestions, setLoadingQuestions] = useState(true);
    const [editingQId, setEditingQId] = useState(null);
    const [editDraft, setEditDraft] = useState(null);
    const [editMode, setEditMode] = useState('quick'); // 'quick' | 'full'
    const [editDirty, setEditDirty] = useState(false);
    const [savingQId, setSavingQId] = useState(null);
    const [deletingQId, setDeletingQId] = useState(null);

    // Intercept destructive actions when there are unsaved edit changes.
    // Returns a promise<bool>: true = proceed, false = cancelled.
    const [discardConfirm, setDiscardConfirm] = useState(null); // { resolve }
    const confirmDiscardIfDirty = () => {
        if (!editDirty) return Promise.resolve(true);
        return new Promise((resolve) => setDiscardConfirm({ resolve }));
    };

    const guardedClose = async () => { if (await confirmDiscardIfDirty()) onClose?.(); };

    // === QuestionCreator embedded ===
    const [creatorOpen, setCreatorOpen] = useState(false);
    const [creatorAutoAi, setCreatorAutoAi] = useState(false);

    // === Improve (admin) ===
    const [improveItems, setImproveItems] = useState([]);
    const [improveLoading, setImproveLoading] = useState(false);
    const [improveProgress, setImproveProgress] = useState(null);
    const [improveSavingId, setImproveSavingId] = useState(null);
    const [improveOneLoadingId, setImproveOneLoadingId] = useState(null);
    const improveCancelRef = useRef(false);
    // Paid-fallback confirmation dialog (triggered when free fails mid-batch)
    const [paidConfirm, setPaidConfirm] = useState(null); // { reason, resolve }

    const [notice, setNotice] = useState(null);
    const [error, setError] = useState(null);

    // (Open training codes moved to ShareCodesPanel "Codes" tab — see
    // Codes d'entraînement section there for generate/revoke/history.)

    // Per-question DOM refs so we can scroll the edited row to the top of the list.
    const qItemRefs = useRef({});
    useEffect(() => {
        if (!editingQId) return;
        const el = qItemRefs.current[editingQId];
        if (!el) return;
        // Wait one frame so QuestionEditPanel has rendered its tall content
        const t = requestAnimationFrame(() => {
            el.scrollIntoView({ block: 'start', behavior: 'smooth' });
        });
        return () => cancelAnimationFrame(t);
    }, [editingQId]);

    const loadQuestions = useCallback(async () => {
        setLoadingQuestions(true);
        try {
            const { data } = await api.get('/custom/question.php', { params: { sous_sujet_id: sousSujet.id } });
            setQuestions(data.data?.questions || []);
        } catch (err) {
            setError(err.response?.data?.message || 'Erreur chargement des questions');
        }
        setLoadingQuestions(false);
    }, [sousSujet.id]);

    useEffect(() => { loadQuestions(); }, [loadQuestions]);

    // Close on ESC
    useEffect(() => {
        const onKey = (e) => { if (e.key === 'Escape' && !editingQId && !editingName) onClose?.(); };
        window.addEventListener('keydown', onKey);
        document.body.style.overflow = 'hidden';
        return () => {
            window.removeEventListener('keydown', onKey);
            document.body.style.overflow = '';
        };
    }, [onClose, editingQId, editingName]);

    // ============ Rename ============
    const startEditName = () => { setLabelDraft(label); setEditingName(true); };
    const cancelEditName = () => { setEditingName(false); setLabelDraft(label); };
    const saveLabel = async () => {
        const trimmed = labelDraft.trim();
        if (trimmed.length < 2) { cancelEditName(); return; }
        if (trimmed === label) { setEditingName(false); return; }
        setSavingLabel(true);
        try {
            await renameSousSujet(sousSujet.custom_ss_id, trimmed);
            setLabel(trimmed);
            setEditingName(false);
            onRefresh?.();
        } catch (err) {
            setError(err.response?.data?.message || 'Erreur renommage');
        }
        setSavingLabel(false);
    };

    // ============ QuestionCreator ============
    const openCreator = (withAi) => { setCreatorAutoAi(withAi); setCreatorOpen(true); };
    const closeCreator = () => { setCreatorOpen(false); setCreatorAutoAi(false); };
    const handleCreated = async () => {
        await loadQuestions();
        onRefresh?.();
    };

    // ============ Improve ============

    // Ask admin to confirm switching to paid mode. Returns a promise<bool>.
    const askPaidConfirmation = (reason) => new Promise((resolve) => {
        setPaidConfirm({ reason: reason || '', resolve });
    });

    const startImprove = async () => {
        setImproveLoading(true);
        setImproveItems([]);
        improveCancelRef.current = false;
        setError(null);
        const paidConfirmedRef = { current: false };
        let cancelledByUser = false;

        try {
            if (questions.length === 0) {
                setNotice('Aucune question à améliorer.');
                setTimeout(() => setNotice(null), 3500);
                setImproveLoading(false);
                return;
            }
            setImproveProgress({ current: 0, total: questions.length });
            const items = [];
            for (let i = 0; i < questions.length; i++) {
                if (improveCancelRef.current) break;
                setImproveProgress({ current: i + 1, total: questions.length });
                const q = questions[i];
                try {
                    const imp = await callAiWithPaidGate('/admin/improve-question.php', {
                        enonce: q.enonce || [],
                        reponses: q.reponses || [],
                        bonne_reponse: q.bonne_reponse,
                        explication: q.explication || [],
                        difficulte: q.difficulte || 2,
                        extra_instructions: ANSWERS_LENGTH_INSTRUCTION,
                    }, paidConfirmedRef, askPaidConfirmation);
                    if (imp.success && imp.data?.improved && !imp.data.no_change) {
                        items.push({
                            id: q.id,
                            label: blocksToText(q.enonce).slice(0, 80) || `Question #${q.id}`,
                            before: {
                                enonce: q.enonce || [],
                                reponses: q.reponses || [],
                                explication: q.explication || [],
                                bonne_reponse: q.bonne_reponse,
                            },
                            after: {
                                enonce: imp.data.improved.enonce || q.enonce,
                                reponses: imp.data.improved.reponses || q.reponses,
                                explication: imp.data.improved.explication || q.explication,
                                bonne_reponse: imp.data.improved.bonne_reponse || q.bonne_reponse,
                            },
                        });
                    }
                } catch (errInner) {
                    if (errInner?.message === '__cancelled__') {
                        cancelledByUser = true;
                        break;
                    }
                    /* other per-question errors: skip */
                }
            }
            setImproveItems(items);
            if (cancelledByUser) {
                setNotice('Analyse interrompue : mode payant refusé.');
                setTimeout(() => setNotice(null), 4000);
            } else if (items.length === 0) {
                setNotice('Aucune amélioration proposée — les réponses sont déjà équilibrées.');
                setTimeout(() => setNotice(null), 3500);
            }
        } catch (err) {
            setError(err.response?.data?.message || err.message);
        }
        setImproveLoading(false);
        setImproveProgress(null);
    };

    // Run the improve flow on a single question (admin-only, one-click from the row).
    const improveOne = async (q) => {
        if (improveOneLoadingId || improveLoading) return;
        setImproveOneLoadingId(q.id);
        setError(null);
        const paidConfirmedRef = { current: false };
        try {
            const imp = await callAiWithPaidGate('/admin/improve-question.php', {
                enonce: q.enonce || [],
                reponses: q.reponses || [],
                bonne_reponse: q.bonne_reponse,
                explication: q.explication || [],
                difficulte: q.difficulte || 2,
                extra_instructions: ANSWERS_LENGTH_INSTRUCTION,
            }, paidConfirmedRef, askPaidConfirmation);
            if (!imp.success) {
                setError(imp.error || 'Erreur IA');
            } else if (imp.data?.no_change || !imp.data?.improved) {
                setNotice('Aucune amélioration proposée pour cette question.');
                setTimeout(() => setNotice(null), 3500);
            } else {
                const item = {
                    id: q.id,
                    label: blocksToText(q.enonce).slice(0, 80) || `Question #${q.id}`,
                    before: {
                        enonce: q.enonce || [],
                        reponses: q.reponses || [],
                        explication: q.explication || [],
                        bonne_reponse: q.bonne_reponse,
                    },
                    after: {
                        enonce: imp.data.improved.enonce || q.enonce,
                        reponses: imp.data.improved.reponses || q.reponses,
                        explication: imp.data.improved.explication || q.explication,
                        bonne_reponse: imp.data.improved.bonne_reponse || q.bonne_reponse,
                    },
                };
                // Replace an existing proposal for the same question, else append
                setImproveItems(prev => {
                    const without = prev.filter(it => it.id !== q.id);
                    return [...without, item];
                });
            }
        } catch (err) {
            if (err?.message !== '__cancelled__') {
                setError(err.response?.data?.message || err.message || 'Erreur IA');
            }
        }
        setImproveOneLoadingId(null);
    };

    const cancelImprove = () => { improveCancelRef.current = true; };
    const changeImproveAfter = (qId, field, value) => {
        setImproveItems(prev => prev.map(it =>
            it.id === qId ? { ...it, after: { ...it.after, [field]: value } } : it
        ));
    };
    const saveImproveOne = async (qId) => {
        const item = improveItems.find(it => it.id === qId);
        if (!item) return;
        setImproveSavingId(qId);
        try {
            await api.put('/custom/question.php', {
                question_id: qId,
                enonce: item.after.enonce,
                reponses: item.after.reponses,
                explication: item.after.explication,
                bonne_reponse: item.after.bonne_reponse,
            });
            setImproveItems(prev => prev.filter(it => it.id !== qId));
            await loadQuestions();
        } catch (err) {
            setError(err.response?.data?.message || 'Erreur save');
        }
        setImproveSavingId(null);
    };
    const saveAllImprove = async () => {
        const ids = improveItems.map(it => it.id);
        for (const id of ids) await saveImproveOne(id);
        onRefresh?.();
    };
    const dismissImprove = (qId) => setImproveItems(prev => prev.filter(it => it.id !== qId));
    const closeImprovePanel = () => setImproveItems([]);

    // ============ Question quick edit (inline simple) ============
    const startEditQuestion = async (q, mode = 'quick') => {
        // If switching between questions, require confirm on unsaved changes.
        if (editingQId && editingQId !== q.id) {
            const ok = await confirmDiscardIfDirty();
            if (!ok) return;
        }
        setEditingQId(q.id);
        setEditMode(mode);
        setEditDirty(false);
        if (mode === 'full') {
            // InlineEditor uses the raw question object directly
            setEditDraft(null);
            return;
        }
        const reps = Array.isArray(q.reponses) ? q.reponses : [];
        const map = {};
        reps.forEach(r => { map[r.id] = r; });
        setEditDraft({
            enonce: blocksToHtml(q.enonce),
            reponses: ['a', 'b', 'c', 'd'].map(id => ({
                id,
                content: blocksToText(map[id]?.content || []),
                _original: map[id]?.content || [],
                _isActive: map[id]?.isActive !== false,
            })),
            bonne_reponse: q.bonne_reponse,
            explication: blocksToHtml(q.explication),
            _originalEnonce: q.enonce || [],
            _originalExplication: q.explication || [],
        });
    };

    const cancelEditQuestion = async () => {
        const ok = await confirmDiscardIfDirty();
        if (!ok) return;
        setEditingQId(null); setEditDraft(null); setEditMode('quick'); setEditDirty(false);
    };
    const forceCancelEditQuestion = () => {
        setEditingQId(null); setEditDraft(null); setEditMode('quick'); setEditDirty(false);
    };

    const saveEditQuestion = async () => {
        if (!editDraft) return;
        const qId = editingQId;
        // Enonce + explication are HTML (from RichInput). Strip tags to validate non-emptiness.
        const enonceHtml = (editDraft.enonce || '').trim();
        const enonceText = enonceHtml.replace(/<[^>]+>/g, '').trim();
        if (!enonceText) { setError('L\'énoncé est requis'); return; }
        const filled = editDraft.reponses.filter(r => r.content.trim());
        if (filled.length < 2) { setError('Au moins 2 réponses sont requises'); return; }
        if (!filled.find(r => r.id === editDraft.bonne_reponse)) {
            setError('La bonne réponse doit être parmi les réponses remplies');
            return;
        }

        setSavingQId(qId);
        setError(null);
        try {
            const newReponses = editDraft.reponses.map(r => ({
                id: r.id,
                content: textToBlocks(r.content, r._original),
                isActive: r.content.trim() ? r._isActive : false,
            })).filter(r => r.isActive);

            await api.put('/custom/question.php', {
                question_id: qId,
                enonce: htmlToBlocks(enonceHtml, editDraft._originalEnonce),
                reponses: newReponses,
                explication: htmlToBlocks(editDraft.explication, editDraft._originalExplication),
                bonne_reponse: editDraft.bonne_reponse,
            });
            forceCancelEditQuestion();
            await loadQuestions();
            onRefresh?.();
        } catch (err) {
            setError(err.response?.data?.message || 'Erreur save');
        }
        setSavingQId(null);
    };

    const deleteQuestion = async (qId) => {
        if (!window.confirm('Supprimer cette question ?')) return;
        setDeletingQId(qId);
        try {
            await api.delete('/custom/question.php', { params: { question_id: qId } });
            await loadQuestions();
            onRefresh?.();
        } catch (err) {
            setError(err.response?.data?.message || 'Erreur suppression');
        }
        setDeletingQId(null);
    };

    const nbQuestions = questions.length;
    const canAddMore = nbQuestions < 50;

    return (
        <div className="sem-overlay" onClick={guardedClose}>
            <div className="sem-modal" onClick={e => e.stopPropagation()}>
                {/* === Header === */}
                <div className="sem-header">
                    <Pencil size={16} />
                    {editingName ? (
                        <span className="sem-rename-wrap">
                            <input
                                className="sem-rename-input"
                                value={labelDraft}
                                onChange={e => setLabelDraft(e.target.value)}
                                onKeyDown={e => {
                                    e.stopPropagation();
                                    if (e.key === 'Enter') { e.preventDefault(); saveLabel(); }
                                    if (e.key === 'Escape') { e.preventDefault(); cancelEditName(); }
                                }}
                                disabled={savingLabel}
                                maxLength={100}
                                autoFocus
                            />
                            <button className="sem-icon-btn sem-icon-btn--save" onClick={saveLabel} disabled={savingLabel} title="Valider">
                                {savingLabel ? <Loader2 size={13} className="spin" /> : <Save size={13} />}
                            </button>
                            <button className="sem-icon-btn sem-icon-btn--cancel" onClick={cancelEditName} disabled={savingLabel} title="Annuler">
                                <X size={13} />
                            </button>
                        </span>
                    ) : (
                        <>
                            <span className="sem-title">{label}</span>
                            <button className="sem-icon-btn" onClick={startEditName} title="Renommer le sujet">
                                <PenLine size={13} />
                            </button>
                        </>
                    )}
                    <span className="sem-count">{nbQuestions}/50 questions</span>
                    <button className="sem-close" onClick={guardedClose} aria-label="Fermer"><X size={18} /></button>
                </div>

                {sousSujet?.origin_type === 'COPY' && sousSujet?.origin_label && (
                    <div className="sem-notice sem-notice--info" style={{ fontSize: '0.82em' }}>
                        <Sparkles size={13} /> {sousSujet.origin_label}
                    </div>
                )}

                {/* Documents sources (multi-doc ou single-doc) */}
                {sousSujet?.documents && sousSujet.documents.length > 0 && (
                    <div className="sem-notice sem-notice--info" style={{ fontSize: '0.82em', display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                        <Sparkles size={13} />
                        <span>
                            {sousSujet.documents.length === 1
                                ? 'Document source :'
                                : `Compilation de ${sousSujet.documents.length} documents sources :`}
                        </span>
                        <SujetDocsButton documents={sousSujet.documents} />
                    </div>
                )}

                {notice && <div className="sem-notice sem-notice--info"><Sparkles size={13} /> {notice}</div>}
                {error && <div className="sem-notice sem-notice--error" onClick={() => setError(null)}><AlertTriangle size={13} /> {error}</div>}

                {/* === Add question section === */}
                <div className="sem-section sem-section--inline">
                    {!creatorOpen && (
                        <>
                            <div className="sem-section-title">Ajouter une question</div>
                            <div className="sem-add-actions">
                                <button
                                    className="btn btn-sm btn-primary"
                                    onClick={() => openCreator(false)}
                                    disabled={!canAddMore}
                                >
                                    <Plus size={14} /> Ajouter une question
                                </button>
                                {!canAddMore && <span className="sem-hint">Limite de 50 questions atteinte.</span>}
                            </div>
                        </>
                    )}
                    {creatorOpen && (
                        <QuestionCreator
                            sousSujetId={sousSujet.id}
                            thematiqueId={thematiqueId}
                            onCreated={handleCreated}
                            onClose={closeCreator}
                            currentCount={nbQuestions}
                            autoGenerateAi={false}
                        />
                    )}
                </div>

                {/* === Improve section (admin only) === */}
                {isAdmin && (
                    <div className="sem-section">
                        <div className="sem-section-title">
                            Améliorer les réponses <span className="sem-admin-badge">admin</span>
                        </div>
                        <div className="sem-add-actions">
                            {improveLoading ? (
                                <>
                                    <span className="sem-hint"><Loader2 size={13} className="spin" /> Génération {improveProgress ? `${improveProgress.current}/${improveProgress.total}` : '...'}</span>
                                    <button className="btn btn-sm btn-ghost" onClick={cancelImprove}>Annuler</button>
                                </>
                            ) : (
                                <button
                                    className="btn btn-sm btn-outline"
                                    onClick={startImprove}
                                    disabled={nbQuestions === 0 || improveItems.length > 0}
                                    title="Reformuler les réponses de toutes les questions pour équilibrer leur longueur"
                                >
                                    <Wand2 size={13} /> Lancer l'analyse IA
                                </button>
                            )}
                        </div>
                        {improveItems.length > 0 && (
                            <QuickEditDiffPanel
                                title="Améliorations proposées"
                                icon={<Sparkles size={16} />}
                                accent="#8B5CF6"
                                items={improveItems}
                                busyId={improveSavingId}
                                onChangeAfter={changeImproveAfter}
                                onSaveOne={saveImproveOne}
                                onDismissOne={dismissImprove}
                                onSaveAll={saveAllImprove}
                                onClose={closeImprovePanel}
                            />
                        )}
                    </div>
                )}

                {/* === Questions list (quick edit inline) === */}
                <div className="sem-section">
                    <div className="sem-section-title">
                        Questions existantes {!loadingQuestions && `(${nbQuestions})`}
                    </div>

                    {loadingQuestions && <div className="sem-loading"><Loader2 size={14} className="spin" /> Chargement...</div>}

                    {!loadingQuestions && nbQuestions === 0 && (
                        <div className="sem-empty">Aucune question pour le moment. Utilisez les boutons ci-dessus pour en ajouter.</div>
                    )}

                    {!loadingQuestions && nbQuestions > 0 && (
                        <div className="sem-q-list">
                            {questions.map((q, idx) => {
                                const isEditing = editingQId === q.id;
                                const isBusy = savingQId === q.id || deletingQId === q.id;

                                // Full editor (QuestionEditPanel) — paragraphs/images/renderers, difficulty
                                // Same panel as in-quiz review, no admin-only filters (matière / sous-sujet / ordre…)
                                if (isEditing && editMode === 'full') {
                                    return (
                                        <div
                                            key={q.id}
                                            ref={el => { qItemRefs.current[q.id] = el; }}
                                            className={`sem-q-item sem-q-item--editing${editDirty ? ' sem-q-item--dirty' : ''}`}
                                        >
                                            <div className="sem-q-row">
                                                <span className="sem-q-idx">#{idx + 1}</span>
                                            </div>
                                            <QuestionEditPanel
                                                question={q}
                                                correctData={{ correct: q.bonne_reponse, explication: q.explication }}
                                                source="custom"
                                                initialEditing
                                                onDirtyChange={setEditDirty}
                                                onSaved={async () => {
                                                    await loadQuestions();
                                                    forceCancelEditQuestion();
                                                    onRefresh?.();
                                                }}
                                                onCancel={cancelEditQuestion}
                                                onDeleted={async () => {
                                                    await loadQuestions();
                                                    forceCancelEditQuestion();
                                                    onRefresh?.();
                                                }}
                                            />
                                        </div>
                                    );
                                }

                                if (isEditing && editDraft) {
                                    return (
                                        <div
                                            key={q.id}
                                            ref={el => { qItemRefs.current[q.id] = el; }}
                                            className={`sem-q-item sem-q-item--editing${editDirty ? ' sem-q-item--dirty' : ''}`}
                                        >
                                            <div className="sem-q-row">
                                                <span className="sem-q-idx">#{idx + 1}</span>
                                                <div className="sem-q-actions">
                                                    <button className="sem-icon-btn sem-icon-btn--save" onClick={saveEditQuestion} disabled={isBusy} title="Sauvegarder">
                                                        {isBusy ? <Loader2 size={13} className="spin" /> : <Save size={13} />}
                                                    </button>
                                                    <button className="sem-icon-btn sem-icon-btn--cancel" onClick={cancelEditQuestion} disabled={isBusy} title="Annuler">
                                                        <X size={13} />
                                                    </button>
                                                </div>
                                            </div>
                                            <label className="sem-q-label">Énoncé</label>
                                            <RichInput
                                                value={editDraft.enonce}
                                                onChange={html => { setEditDraft(d => ({ ...d, enonce: html })); setEditDirty(true); }}
                                                rows={2}
                                                disabled={isBusy}
                                            />
                                            <label className="sem-q-label">Réponses (cochez la bonne)</label>
                                            {editDraft.reponses.map((r) => (
                                                <div key={r.id} className="sem-q-rep">
                                                    <label className="sem-q-radio">
                                                        <input
                                                            type="radio"
                                                            name={`br_${q.id}`}
                                                            checked={editDraft.bonne_reponse === r.id}
                                                            onChange={() => { setEditDraft(d => ({ ...d, bonne_reponse: r.id })); setEditDirty(true); }}
                                                            disabled={isBusy}
                                                        />
                                                        <span className={`sem-q-letter ${editDraft.bonne_reponse === r.id ? 'sem-q-letter--active' : ''}`}>
                                                            {r.id.toUpperCase()}
                                                        </span>
                                                    </label>
                                                    <input
                                                        type="text"
                                                        className="sem-q-input"
                                                        value={r.content}
                                                        onChange={e => {
                                                            setEditDraft(d => ({
                                                                ...d,
                                                                reponses: d.reponses.map(rep => rep.id === r.id ? { ...rep, content: e.target.value } : rep)
                                                            }));
                                                            setEditDirty(true);
                                                        }}
                                                        disabled={isBusy}
                                                        placeholder={`Réponse ${r.id.toUpperCase()}`}
                                                    />
                                                </div>
                                            ))}
                                            <label className="sem-q-label">Explication <span className="sem-q-label-opt">(optionnel)</span></label>
                                            <RichInput
                                                value={editDraft.explication}
                                                onChange={html => { setEditDraft(d => ({ ...d, explication: html })); setEditDirty(true); }}
                                                rows={2}
                                                disabled={isBusy}
                                            />
                                        </div>
                                    );
                                }

                                // Read-only row
                                const enonceText = blocksToText(q.enonce);
                                return (
                                    <div
                                        key={q.id}
                                        ref={el => { qItemRefs.current[q.id] = el; }}
                                        className="sem-q-item"
                                    >
                                        <div className="sem-q-row">
                                            <span className="sem-q-idx">#{idx + 1}</span>
                                            <span className="sem-q-preview">{enonceText || '—'}</span>
                                            <div className="sem-q-actions">
                                                {isAdmin && (
                                                    <button
                                                        className="sem-icon-btn sem-icon-btn--magic"
                                                        onClick={() => improveOne(q)}
                                                        title="Lancer l'analyse IA sur cette question"
                                                        disabled={isBusy || improveOneLoadingId === q.id || improveLoading}
                                                    >
                                                        {improveOneLoadingId === q.id ? <Loader2 size={13} className="spin" /> : <Wand2 size={13} />}
                                                    </button>
                                                )}
                                                <button className="sem-icon-btn" onClick={() => startEditQuestion(q, 'quick')} title="Édition rapide (inline)" disabled={isBusy}>
                                                    <Eye size={13} />
                                                </button>
                                                <button className="sem-icon-btn" onClick={() => startEditQuestion(q, 'full')} title="Édition complète (paragraphes, images, réponses…)" disabled={isBusy}>
                                                    <PenLine size={13} />
                                                </button>
                                                <button className="sem-icon-btn sem-icon-btn--danger" onClick={() => deleteQuestion(q.id)} title="Supprimer" disabled={isBusy}>
                                                    {deletingQId === q.id ? <Loader2 size={13} className="spin" /> : <Trash2 size={13} />}
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </div>

                {/* Danger zone — mise en obsolète du sujet (déplacé depuis la carte
                    pour minimiser les actions visibles en miniature). */}
                {onObsolete && sousSujet?.custom_ss_id && (
                    <div style={{
                        margin: 12, paddingTop: 12,
                        borderTop: '1px dashed var(--card-border, #e5e7eb)',
                        display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12,
                        flexWrap: 'wrap',
                    }}>
                        <div style={{ fontSize: '0.82em', color: 'var(--text-muted)', flex: 1, minWidth: 200 }}>
                            <strong>Supprimer ce sujet</strong> — il sera mis en obsolète et masqué des listes.
                            Vous pourrez le réanimer plus tard depuis le toggle « Obsolètes » de la catégorie.
                        </div>
                        <button
                            type="button"
                            className="btn btn-sm btn-outline"
                            style={{ color: '#ef4444', borderColor: '#ef4444', flexShrink: 0 }}
                            onClick={async () => {
                                if (editDirty) {
                                    const ok = await confirmDiscardIfDirty();
                                    if (!ok) return;
                                }
                                onObsolete();
                            }}
                        >
                            <Trash2 size={13} /> Supprimer le sujet
                        </button>
                    </div>
                )}
            </div>

            {/* Unsaved-changes confirmation — replaces window.confirm */}
            <ConfirmDialog
                open={!!discardConfirm}
                title="Modifications non sauvegardées"
                message={(
                    <>
                        Vos modifications en cours n'ont pas été sauvegardées.<br/>
                        Voulez-vous les <strong>abandonner</strong> ?
                    </>
                )}
                confirmLabel="Abandonner"
                cancelLabel="Continuer l'édition"
                variant="danger"
                onConfirm={() => {
                    const r = discardConfirm?.resolve;
                    setDiscardConfirm(null);
                    setEditDirty(false);
                    r?.(true);
                }}
                onCancel={() => {
                    const r = discardConfirm?.resolve;
                    setDiscardConfirm(null);
                    r?.(false);
                }}
            />

            {/* Paid-fallback confirmation — admin opts into paid mode after free failure */}
            <ConfirmDialog
                open={!!paidConfirm}
                title="Génération gratuite indisponible"
                message={paidConfirm ? (
                    <>
                        Le mode gratuit est temporairement indisponible{paidConfirm.reason ? <> ({paidConfirm.reason})</> : ''}.<br/><br/>
                        Voulez-vous relancer <strong>en mode payant</strong> ? Les questions restantes du lot utiliseront aussi le mode payant.
                    </>
                ) : ''}
                confirmLabel="Continuer en payant"
                cancelLabel="Annuler"
                variant="primary"
                onConfirm={() => { const r = paidConfirm?.resolve; setPaidConfirm(null); r?.(true); }}
                onCancel={() => { const r = paidConfirm?.resolve; setPaidConfirm(null); r?.(false); }}
            />
        </div>
    );
}

/**
 * Inline sub-component — shows the two "Add question" buttons + the lifetime
 * "Questions IA" pill (per-thematique cap + any remaining pack credits).
 */
function AddQuestionActions({ canAddMore, creatorOpen, thematiqueId, onOpenManual, onOpenAi }) {
    const { monoCap, monoUsed, monoRemaining } = useCreditPacks(thematiqueId);

    // Lifetime usage = base cap + pack credits (both per-thematique in v2)
    const baseRemaining = Math.max(0, (monoCap ?? 0) - monoUsed);
    const totalRemaining = baseRemaining + monoRemaining;
    const totalLimit = (monoCap ?? 0) + monoRemaining + (monoUsed > (monoCap ?? 0) ? 0 : 0);
    // "used / limit" pill shown as used / capAt-base-level (pack credits are
    // additional safety net, displayed in the tooltip)
    const pillQuota = monoCap != null ? {
        used:      Math.min(monoUsed, monoCap),
        limit:     monoCap,
        remaining: baseRemaining,
        exceeded:  monoUsed >= monoCap && monoRemaining <= 0,
        enabled:   true,
    } : null;
    const aiExceeded = totalRemaining <= 0 && monoCap != null;

    return (
        <div className="sem-add-actions">
            <button
                className="btn btn-sm btn-outline"
                onClick={onOpenManual}
                disabled={!canAddMore || creatorOpen}
            >
                <Plus size={13} /> Manuelle
            </button>
            <button
                className="btn btn-sm btn-outline"
                onClick={onOpenAi}
                disabled={!canAddMore || creatorOpen || aiExceeded}
                title={aiExceeded
                    ? `Quota questions IA atteint (${monoUsed}/${monoCap}) — achetez un Pack IA.`
                    : "Ouvrir le formulaire pré-rempli par l'IA (validation avant ajout)"}
            >
                <Plus size={13} /> Générée par IA (brouillon)
            </button>
            <AiQuotaPill quota={pillQuota} label="Questions IA" />
            {monoRemaining > 0 && (
                <span className="sem-hint" title="Crédits Pack IA actifs">
                    +{monoRemaining} pack
                </span>
            )}
            <BuyPackButton thematiqueId={thematiqueId} compact />
            {!canAddMore && <span className="sem-hint">Limite de 50 questions atteinte.</span>}
        </div>
    );
}
